import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  CalendarPlus,
  ChevronDown,
  ChevronUp,
  Users,
  Trash2,
  Loader2,
  Settings2,
  Plus,
} from 'lucide-react';
import { useInstitution } from '../../contexts/InstitutionContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissao } from '../../hooks/usePermissao';
import { useToast, useConfirm } from '../../Components/Feedback';
import { Input, Select, Button, Modal } from '../../Components/ui';
import FotoAluno from '../../Components/FotoAluno';
import { LAUDOS_DISPONIVEIS, parseLaudos } from '../../Components/AlunoBadges';
import { API_URL } from '../../api/config';

const turnos = ['Manhã', 'Tarde'];
const diasSemana = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
const horarios = ['HR 1', 'HR 2', 'HR 3'];

// Mesma regra de "compatível com o turno do aluno" do backend
// (backend/regras-matricula.js) — turma de turno "Noite" é sempre um ensaio e
// vale pra qualquer aluno, o resto tem que bater com o turno dele.
const compativel = (turnoAluno, turnoTurma) => {
  if (!turnoAluno || !turnoTurma) return true;
  if (String(turnoTurma).trim().toLowerCase() === 'noite') return true;
  return String(turnoAluno).trim().toLowerCase() === String(turnoTurma).trim().toLowerCase();
};

const normalizarParaComparacao = (str) =>
  String(str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const ESTADO_INICIAL = {
  nome: '',
  foto_url: null,
  data_nascimento: '',
  data_cadastro: '',
  sexo: '',
  telefone: '',
  turma: '',
  turno: '',
  transporte: '',
  status: 'ativo',
  motivo_desistencia: '',
  acompanhamento: '',
  ponto: '',
  ponto_desembarque: '',
  informacoes_gerais: '',
  escola_atual: '',
  bolsista: false,
  lider_servidor: false,
  laudos: [],
  cuidados_medicos: '',
  Inf: '',
  nivel: '',
  subnivel: '',
  situacao_matricula: '',
  situacao_divida: '',
  observacao_saude: '',
  responsavel_nome: '',
  responsavel_cpf: '',
  responsavel_rg: '',
  responsavel_data_nascimento: '',
  responsavel_email: '',
  responsavel_endereco: '',
  responsavel_bairro: '',
  responsavel_cep: '',
  responsavel_telefone: '',
  renda_familiar: '',
  moradores_casa: '',
  moradia_tipo: '',
  trabalho_responsaveis: '',
  recebe_beneficio: '',
  nis: '',
  vulnerabilidade: 'nao_informado',
  orgaos_acompanhamento: '',
  observacoes_social: '',
};

// Data "YYYY-MM-DDTHH:mm:ss.sssZ" (como o backend devolve) -> "YYYY-MM-DD"
// (o que o <input type="date"> espera) — sem isso o campo vem em branco.
const paraInputDate = (valor) => (valor ? String(valor).slice(0, 10) : '');

// Página de cadastro/edição completa de aluno, cobrindo os mesmos campos que
// a importação em massa por planilha aceita (ver Passo 5b-5e de POST/PUT
// /api/alunos no backend). Antes era um modal (Cadastrar) — cresceu demais
// pra continuar cabendo numa janela sobreposta. A edição (`/gerenciar-
// matriculas/aluno/:id/editar`) reusa o mesmo formulário: com `:id` na URL,
// busca os dados atuais do aluno e troca POST por PUT ao salvar.
function CadastrarAluno() {
  const navigate = useNavigate();
  const { id } = useParams();
  const modoEdicao = Boolean(id);
  const { institutionId } = useInstitution();
  const { user } = useAuth();
  const isCoordenadorOuMaster = user?.perfil === 'coordenador' || user?.perfil === 'master';
  const { podeCriar, podeEditar } = usePermissao('/gerenciar-matriculas');
  const podeCriarEfetivo = isCoordenadorOuMaster || podeCriar;
  const podeEditarEfetivo = isCoordenadorOuMaster || podeEditar;
  const semPermissao = modoEdicao ? !podeEditarEfetivo : !podeCriarEfetivo;
  const toast = useToast();
  const confirm = useConfirm();
  const token = localStorage.getItem('authToken');
  const authHeaders = useMemo(
    () => ({
      'x-institution-id': institutionId,
      Authorization: `Bearer ${token}`,
    }),
    [institutionId, token],
  );

  const [novoAluno, setNovoAluno] = useState(ESTADO_INICIAL);
  const [salvando, setSalvando] = useState(false);
  const [errosCampos, setErrosCampos] = useState({});
  const [carregandoAluno, setCarregandoAluno] = useState(modoEdicao);

  // Ano exibido no título "Situação Anual (...)" — chute inicial pelo
  // relógio do aparelho, corrigido pela data do SERVIDOR (GET /api/hoje)
  // assim que a tela abre, pro título nunca divergir do ano de verdade.
  const [anoSituacao, setAnoSituacao] = useState(() => new Date().getFullYear());
  useEffect(() => {
    fetch(`${API_URL}/api/hoje`, { headers: authHeaders })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.hoje) setAnoSituacao(Number(data.hoje.slice(0, 4)));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [saudeExistente, setSaudeExistente] = useState([]);
  const [removendoSaudeId, setRemovendoSaudeId] = useState(null);
  const [matriculasAtuais, setMatriculasAtuais] = useState([]);
  const [statusOriginal, setStatusOriginal] = useState('ativo');

  const [mostrarMatriculas, setMostrarMatriculas] = useState(false);
  const [atividades, setAtividades] = useState(null); // null = ainda não buscou
  const [carregandoAtividades, setCarregandoAtividades] = useState(false);
  const [matriculasEscolhidas, setMatriculasEscolhidas] = useState({}); // "dia|horario" -> id_atividade

  // Pontos de embarque cadastrados pela instituição (ver backend/pontos-embarque.js)
  // — cada instituição mantém a própria lista, por isso recarrega sempre
  // que institutionId mudar.
  const [pontos, setPontos] = useState([]);
  const [modalPontosAberto, setModalPontosAberto] = useState(false);
  const [novoPontoNome, setNovoPontoNome] = useState('');
  const [salvandoPonto, setSalvandoPonto] = useState(false);

  const carregarPontos = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/pontos-embarque`, { headers: authHeaders });
      if (resp.ok) {
        const body = await resp.json();
        setPontos(body?.data ?? (Array.isArray(body) ? body : []));
      }
    } catch {
      // Silencioso: pior caso, o dropdown fica vazio e ainda dá pra
      // digitar um ponto novo direto pelo modal de gerenciar.
    }
  }, [authHeaders]);

  useEffect(() => {
    carregarPontos();
  }, [carregarPontos]);

  const adicionarPonto = async () => {
    const nome = novoPontoNome.trim();
    if (!nome) return;
    setSalvandoPonto(true);
    try {
      const resp = await fetch(`${API_URL}/api/pontos-embarque`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ nome }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Erro ao adicionar ponto.');
      setNovoPontoNome('');
      toast.success(data?.message || 'Ponto cadastrado com sucesso.');
      await carregarPontos();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvandoPonto(false);
    }
  };

  const removerPonto = async (ponto) => {
    if (
      !(await confirm(
        `Remover "${ponto.nome}" da lista de pontos? Quem já está com esse ponto no cadastro não é afetado.`,
        { title: 'Remover ponto', confirmText: 'Remover' },
      ))
    )
      return;
    try {
      const resp = await fetch(`${API_URL}/api/pontos-embarque/${ponto.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Erro ao remover ponto.');
      toast.success(data?.message || 'Ponto removido com sucesso.');
      await carregarPontos();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Transportes cadastrados pela instituição (ver backend/src/modules/transportes)
  // — cada instituição mantém a própria lista, por isso recarrega sempre
  // que institutionId mudar.
  const [transportes, setTransportes] = useState([]);
  const [modalTransportesAberto, setModalTransportesAberto] = useState(false);
  const [novoTransporteNome, setNovoTransporteNome] = useState('');
  const [salvandoTransporte, setSalvandoTransporte] = useState(false);

  const carregarTransportes = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/transportes`, { headers: authHeaders });
      if (resp.ok) {
        const body = await resp.json();
        setTransportes(body?.data ?? (Array.isArray(body) ? body : []));
      }
    } catch {
      // Silencioso
    }
  }, [authHeaders]);

  useEffect(() => {
    carregarTransportes();
  }, [carregarTransportes]);

  const adicionarTransporte = async () => {
    const nome = novoTransporteNome.trim();
    if (!nome) return;
    setSalvandoTransporte(true);
    try {
      const resp = await fetch(`${API_URL}/api/transportes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ nome }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Erro ao adicionar transporte.');
      setNovoTransporteNome('');
      toast.success(data?.message || 'Transporte cadastrado com sucesso.');
      await carregarTransportes();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvandoTransporte(false);
    }
  };

  const removerTransporte = async (transporte) => {
    if (
      !(await confirm(
        `Remover "${transporte.nome}" da lista de transportes? Quem já está com esse transporte no cadastro não é afetado.`,
        { title: 'Remover transporte', confirmText: 'Remover' },
      ))
    )
      return;
    try {
      const resp = await fetch(`${API_URL}/api/transportes/${transporte.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Erro ao remover transporte.');
      toast.success(data?.message || 'Transporte removido com sucesso.');
      await carregarTransportes();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Alunos existentes na instituição para checagem rápida de duplicidade
  const [alunosExistentes, setAlunosExistentes] = useState([]);

  const carregarAlunosExistentes = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/alunos?status=todos`, { headers: authHeaders });
      if (resp.ok) {
        const body = await resp.json();
        setAlunosExistentes(body?.data ?? (Array.isArray(body) ? body : []));
      }
    } catch {
      // Silencioso
    }
  }, [authHeaders]);

  useEffect(() => {
    carregarAlunosExistentes();
  }, [carregarAlunosExistentes]);

  const validarNome = useCallback(
    async (valorNome, emitirAlerta = true) => {
      const nomeLimpo = (valorNome !== undefined ? valorNome : novoAluno.nome || '').trim();
      if (!nomeLimpo) {
        setErrosCampos((prev) => ({ ...prev, nome: 'O campo "nome" é obrigatório.' }));
        return false;
      }

      let lista = alunosExistentes;
      if (!lista || lista.length === 0) {
        try {
          const checagem = await fetch(`${API_URL}/api/alunos?status=todos`, { headers: authHeaders });
          if (checagem.ok) {
            const bodyChecagem = await checagem.json();
            lista = bodyChecagem?.data ?? (Array.isArray(bodyChecagem) ? bodyChecagem : []);
            setAlunosExistentes(lista);
          }
        } catch {
          // segue
        }
      }

      const nomeNormalizado = normalizarParaComparacao(nomeLimpo);
      const jaExiste = lista.find(
        (a) =>
          normalizarParaComparacao(a.nome) === nomeNormalizado && String(a.id) !== String(id),
      );

      if (jaExiste) {
        const rotuloStatus =
          jaExiste.status === 'inativo'
            ? 'Inativo'
            : jaExiste.status === 'espera'
              ? 'Em espera'
              : 'Ativo';
        const msg = `O inscrito "${jaExiste.nome}" já está cadastrado nesta instituição (Status: ${rotuloStatus}).`;
        setErrosCampos((prev) => ({ ...prev, nome: msg }));
        if (emitirAlerta) {
          toast.error(`${msg} Verifique o campo "Nome Completo" ou reative o cadastro existente.`, 8000);
        }
        return false;
      }

      setErrosCampos((prev) => {
        if (!prev.nome) return prev;
        const novo = { ...prev };
        delete novo.nome;
        return novo;
      });
      return true;
    },
    [alunosExistentes, authHeaders, id, novoAluno.nome, toast],
  );

  // Carrega os dados atuais do aluno (só no modo edição) — dados diretos +
  // nível aberto + situação anual do ano corrente + saúde + responsável,
  // cada um com seu próprio formato (ver as rotas GET '/:id/...' no backend).
  useEffect(() => {
    if (!modoEdicao) return;
    let cancelado = false;

    (async () => {
      setCarregandoAluno(true);
      try {
        // Ano vem do SERVIDOR (GET /api/hoje), não do relógio do aparelho — se
        // o relógio/fuso do aparelho estiver errado, buscar a situação anual do
        // ano do CLIENTE podia não achar nenhum registro (o ano real ainda nem
        // tem status lançado) e mostrar a tela como se o aluno não tivesse
        // situação nenhuma no ano corrente.
        const anoAtualFallback = new Date().getFullYear();
        const [
          alunoResp,
          niveisResp,
          situacaoResp,
          saudeResp,
          responsavelResp,
          socialResp,
          matriculasResp,
          hojeResp,
        ] = await Promise.all([
          fetch(`${API_URL}/api/alunos/${id}`, { headers: authHeaders }),
          fetch(`${API_URL}/api/alunos/${id}/niveis`, { headers: authHeaders }),
          fetch(`${API_URL}/api/alunos/${id}/situacao-anual`, { headers: authHeaders }),
          fetch(`${API_URL}/api/alunos/${id}/saude`, { headers: authHeaders }),
          fetch(`${API_URL}/api/alunos/${id}/responsavel`, { headers: authHeaders }),
          fetch(`${API_URL}/api/alunos/${id}/social`, { headers: authHeaders }),
          fetch(`${API_URL}/api/matriculas/aluno/${id}`, { headers: authHeaders }),
          fetch(`${API_URL}/api/hoje`, { headers: authHeaders }),
        ]);
        if (!alunoResp.ok) throw new Error('Inscrito não encontrado.');

        const alunoRaw = await alunoResp.json();
        const aluno = alunoRaw?.data ?? alunoRaw;
        const niveisRaw = niveisResp.ok ? await niveisResp.json() : [];
        const niveis = niveisRaw?.data ?? (Array.isArray(niveisRaw) ? niveisRaw : []);
        const situacoesRaw = situacaoResp.ok ? await situacaoResp.json() : [];
        const situacoes = situacoesRaw?.data ?? (Array.isArray(situacoesRaw) ? situacoesRaw : []);
        const saudeRaw = saudeResp.ok ? await saudeResp.json() : [];
        const saude = saudeRaw?.data ?? (Array.isArray(saudeRaw) ? saudeRaw : []);
        const responsavelRaw = responsavelResp.ok ? await responsavelResp.json() : null;
        const responsavel = responsavelRaw?.data ?? responsavelRaw;
        const socialRaw = socialResp.ok ? await socialResp.json() : null;
        const social = socialRaw?.data ?? socialRaw;
        const hojeServidorRaw = hojeResp.ok ? await hojeResp.json() : null;
        const hojeServidor = hojeServidorRaw?.data ?? hojeServidorRaw;
        const anoAtual = hojeServidor?.hoje
          ? Number(hojeServidor.hoje.slice(0, 4))
          : anoAtualFallback;
        const matriculasRaw = matriculasResp.ok ? await matriculasResp.json() : [];
        const matriculas = matriculasRaw?.data ?? (Array.isArray(matriculasRaw) ? matriculasRaw : []);

        if (cancelado) return;

        const nivelAtual = niveis.find((n) => !n.data_fim) || null;
        const situacaoAtual = situacoes.find((s) => s.ano === anoAtual) || null;

        setNovoAluno({
          nome: aluno.nome || '',
          foto_url: aluno.foto_url || null,
          data_nascimento: paraInputDate(aluno.data_nascimento),
          data_cadastro: paraInputDate(aluno.data_cadastro),
          sexo: aluno.sexo || '',
          telefone: aluno.telefone || '',
          turma: aluno.turma || '',
          turno: aluno.turno || '',
          transporte: aluno.transporte || '',
          status: aluno.status || 'ativo',
          motivo_desistencia: aluno.motivo_desistencia || '',
          acompanhamento: aluno.acompanhamento || '',
          ponto: aluno.ponto || '',
          ponto_desembarque: aluno.ponto_desembarque || aluno.ponto || '',
          informacoes_gerais: aluno.informacoes_gerais || '',
          escola_atual: aluno.escola_atual || '',
          bolsista: Boolean(aluno.bolsista),
          lider_servidor: Boolean(aluno.lider_servidor),
          laudos: parseLaudos(aluno.laudos),
          cuidados_medicos: aluno.cuidados_medicos || '',
          Inf: aluno.Inf || '',
          nivel: nivelAtual ? String(nivelAtual.nivel) : '',
          subnivel: nivelAtual?.subnivel ? String(nivelAtual.subnivel) : '',
          situacao_matricula: situacaoAtual?.situacao_matricula || '',
          situacao_divida: situacaoAtual?.situacao_divida || '',
          observacao_saude: '',
          responsavel_nome: responsavel?.nome || '',
          responsavel_cpf: responsavel?.cpf || '',
          responsavel_rg: responsavel?.rg || '',
          responsavel_data_nascimento: paraInputDate(responsavel?.data_nascimento),
          responsavel_email: responsavel?.email || '',
          responsavel_endereco: responsavel?.endereco || '',
          responsavel_bairro: responsavel?.bairro || '',
          responsavel_cep: responsavel?.cep || '',
          responsavel_telefone: responsavel?.telefone || '',
          renda_familiar:
            social?.renda_familiar !== null && social?.renda_familiar !== undefined
              ? String(social.renda_familiar)
              : '',
          moradores_casa:
            social?.moradores_casa !== null && social?.moradores_casa !== undefined
              ? String(social.moradores_casa)
              : '',
          moradia_tipo: social?.moradia_tipo || '',
          trabalho_responsaveis: social?.trabalho_responsaveis || '',
          recebe_beneficio: social?.recebe_beneficio || '',
          nis: social?.nis || '',
          vulnerabilidade: social?.vulnerabilidade || 'nao_informado',
          orgaos_acompanhamento: social?.orgaos_acompanhamento || '',
          observacoes_social: social?.observacoes_social || '',
        });
        setSaudeExistente(saude);
        setMatriculasAtuais(matriculas);
        setStatusOriginal(aluno.status || 'ativo');
      } catch (error) {
        toast.error(error.message);
        navigate('/gerenciar-matriculas');
      } finally {
        if (!cancelado) setCarregandoAluno(false);
      }
    })();

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, modoEdicao]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNovoAluno((prev) => ({ ...prev, [name]: value }));
    if (errosCampos[name]) {
      setErrosCampos((prev) => {
        const novo = { ...prev };
        delete novo[name];
        return novo;
      });
    }
    // Trocar o turno pode tornar inválida uma matrícula já escolhida (uma
    // turma que só era compatível com o turno anterior) — limpa pra não
    // deixar uma seleção "fantasma" que não aparece mais na lista.
    if (name === 'turno') setMatriculasEscolhidas({});
  };

  // Nível/subnível: só um único dígito (0-9), nada de ponto, vírgula ou
  // ponto e vírgula — bloqueia a tecla na hora (onKeyDown) e ainda limpa
  // qualquer caractere que escape disso (colar texto, por exemplo).
  const handleDigitoUnicoKeyDown = (e) => {
    const teclasPermitidas = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];
    if (teclasPermitidas.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleDigitoUnicoChange = (e) => {
    const { name, value } = e.target;
    const digitoUnico = value.replace(/\D/g, '').slice(0, 1);
    setNovoAluno((prev) => ({ ...prev, [name]: digitoUnico }));
  };

  const handleRemoverSaude = useCallback(
    async (saudeId) => {
      setRemovendoSaudeId(saudeId);
      try {
        const resp = await fetch(`${API_URL}/api/alunos/${id}/saude/${saudeId}`, {
          method: 'DELETE',
          headers: authHeaders,
        });
        if (!resp.ok) throw new Error('Falha ao remover a observação.');
        setSaudeExistente((prev) => prev.filter((s) => s.id !== saudeId));
      } catch (error) {
        toast.error(error.message);
      } finally {
        setRemovendoSaudeId(null);
      }
    },
    [id, authHeaders, toast],
  );

  const handleToggleLaudo = (laudoId) => {
    setNovoAluno((prev) => {
      const atuais = Array.isArray(prev.laudos) ? prev.laudos : [];
      const novoArray = atuais.includes(laudoId)
        ? atuais.filter((l) => l !== laudoId)
        : [...atuais, laudoId];
      return { ...prev, laudos: novoArray };
    });
  };

  const abrirInsercaoMatriculas = async () => {
    const abrindo = !mostrarMatriculas;
    setMostrarMatriculas(abrindo);
    if (abrindo && atividades === null) {
      setCarregandoAtividades(true);
      try {
        const resp = await fetch(`${API_URL}/api/atividades`, { headers: authHeaders });
        if (!resp.ok) throw new Error('Falha ao carregar as turmas disponíveis.');
        const body = await resp.json();
        setAtividades(body?.data ?? (Array.isArray(body) ? body : []));
      } catch (error) {
        toast.error(error.message);
        setAtividades([]);
      } finally {
        setCarregandoAtividades(false);
      }
    }
  };

  // Pra cada dia+horário, só as turmas compatíveis com o turno escolhido pro
  // aluno (ensaio à noite entra sempre), e no modo edição também tira quem
  // já está matriculado nesse exato dia+horário — o painel é só pra
  // adicionar matrícula nova, não pra mostrar de novo o que já existe.
  const slotsPorDia = useMemo(() => {
    if (!atividades) return [];
    const ocupados = new Set(matriculasAtuais.map((m) => `${m.dia_semana}|${m.horario}`));
    return diasSemana
      .map((dia) => ({
        dia,
        horarios: horarios
          .map((horario) => ({
            horario,
            opcoes: ocupados.has(`${dia}|${horario}`)
              ? []
              : atividades.filter(
                  (a) =>
                    a.dia_semana === dia &&
                    a.horario === horario &&
                    compativel(novoAluno.turno, a.turno),
                ),
          }))
          .filter((h) => h.opcoes.length > 0),
      }))
      .filter((d) => d.horarios.length > 0);
  }, [atividades, novoAluno.turno, matriculasAtuais]);

  const handleEscolherMatricula = (dia, horario, idAtividade) => {
    const chave = `${dia}|${horario}`;
    setMatriculasEscolhidas((prev) => {
      const novo = { ...prev };
      if (idAtividade) novo[chave] = idAtividade;
      else delete novo[chave];
      return novo;
    });
  };

  const totalMatriculasEscolhidas = Object.keys(matriculasEscolhidas).length;

  const voltar = () => navigate('/gerenciar-matriculas');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrosCampos({});
    const nomeLimpo = (novoAluno.nome || '').trim();
    if (!nomeLimpo) {
      setErrosCampos({ nome: 'O campo "nome" é obrigatório.' });
      toast.error('O campo "nome" é obrigatório.');
      const elemento = document.querySelector('input[name="nome"]');
      elemento?.focus();
      return;
    }

    // Turno mudou de um jeito que deixa alguma matrícula ATUAL incompatível
    // (mesma regra `compativel` usada pra filtrar o painel de matricular acima)?
    // Backend encerra essas matrículas automaticamente ao salvar (ver
    // encerrarMatriculasForaDoTurno em backend/status-sync.js) — avisa e
    // pede confirmação ANTES de mandar, pra não pegar o usuário de surpresa.
    // Ensaio (turno Noite) nunca entra nessa lista, porque `compativel`
    // já trata "Noite" como sempre permitido pra qualquer turno de aluno.
    if (modoEdicao) {
      const incompativeis = matriculasAtuais.filter((m) => !compativel(novoAluno.turno, m.turno));
      if (incompativeis.length > 0) {
        const lista = incompativeis
          .map((m) => `• ${m.nome_atividade} (${m.dia_semana} ${m.horario})`)
          .join('\n');
        const confirmou = await confirm(
          `Ao mudar o turno, as seguintes matrículas atuais não serão mais compatíveis e serão encerradas (histórico continua registrado, pode reabrir depois):\n\n${lista}\n\nEnsaios (turno Noite) não são afetados. Deseja continuar?`,
          { title: 'Confirmar troca de turno', confirmText: 'Continuar', tone: 'danger' },
        );
        if (!confirmou) return;
      }

      // Status virando algo diferente de "ativo" (ex.: "Em espera")?
      // Matrícula só vale pra aluno ativo — backend encerra TODAS as
      // matrículas ativas dele automaticamente ao salvar (ver
      // encerrarMatriculasSeNaoAtivo em backend/status-sync.js). Mesmo
      // padrão de confirmação usado pra troca de turno acima.
      if (
        novoAluno.status !== 'ativo' &&
        novoAluno.status !== statusOriginal &&
        matriculasAtuais.length > 0
      ) {
        const lista = matriculasAtuais
          .map((m) => `• ${m.nome_atividade} (${m.dia_semana} ${m.horario})`)
          .join('\n');
        const rotuloStatus = novoAluno.status === 'espera' ? 'Em espera' : 'Inativo';
        const confirmou = await confirm(
          `Ao mudar o status para "${rotuloStatus}", as seguintes matrículas atuais serão encerradas (histórico continua registrado, pode reabrir depois):\n\n${lista}\n\nDeseja continuar?`,
          { title: 'Confirmar mudança de status', confirmText: 'Continuar', tone: 'danger' },
        );
        if (!confirmou) return;
      }
    }

    if (novoAluno.status === 'inativo' && !novoAluno.motivo_desistencia?.trim()) {
      setErrosCampos((prev) => ({
        ...prev,
        motivo_desistencia: 'O motivo da desistência é obrigatório ao inativar o inscrito.',
      }));
      toast.error('Informe o motivo da desistência.');
      const elemento = document.querySelector('input[name="motivo_desistencia"]');
      elemento?.focus();
      return;
    }

    setSalvando(true);
    try {
      // Checagem de duplicado antes de enviar
      const nomeValido = await validarNome(nomeLimpo, true);
      if (!nomeValido) {
        setSalvando(false);
        const elemento = document.querySelector('input[name="nome"]');
        elemento?.focus();
        return;
      }

      const response = await fetch(`${API_URL}/api/alunos${modoEdicao ? `/${id}` : ''}`, {
        method: modoEdicao ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ ...novoAluno, nome: nomeLimpo }),
      });

      const rawResult = await response.json();
      if (!response.ok) {
        const campoErrado = rawResult?.campo;
        const msg = rawResult?.error || rawResult?.message || 'Erro ao salvar inscrito.';
        const detalhes = rawResult?.details
          ? Array.isArray(rawResult.details)
            ? rawResult.details.join(', ')
            : String(rawResult.details)
          : '';

        if (campoErrado) {
          setErrosCampos({ [campoErrado]: msg });
          const elemento = document.querySelector(`[name="${campoErrado}"]`);
          elemento?.focus();
        }

        const msgFinal = campoErrado
          ? `Erro no campo "${campoErrado}": ${msg}${detalhes ? ` (${detalhes})` : ''}`
          : `${msg}${detalhes ? ` (${detalhes})` : ''}`;

        throw new Error(msgFinal);
      }
      const result = rawResult?.data ?? rawResult;

      const idAlvo = modoEdicao ? id : result.id;

      // Matrículas escolhidas no painel "Inserir Matrículas" — reaproveita
      // a mesma rota da grade em lote (POST /api/matriculas), que já bloqueia
      // e reporta qualquer conflito de turno (ver backend/regras-matricula.js).
      if (totalMatriculasEscolhidas > 0) {
        const alteracoes = Object.entries(matriculasEscolhidas).map(([chave, id_atividade]) => {
          const [dia_semana, horario] = chave.split('|');
          return { aluno_id: idAlvo, dia_semana, horario, id_atividade: Number(id_atividade) };
        });
        const respMatriculas = await fetch(`${API_URL}/api/matriculas`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ alteracoes }),
        });
        const rawMatriculas = await respMatriculas.json();
        const resultMatriculas = rawMatriculas?.data ?? rawMatriculas;
        if (respMatriculas.ok && resultMatriculas.conflitos_turno?.length > 0) {
          toast.error(
            `Inscrito salvo, mas ${resultMatriculas.conflitos_turno.length} matrícula(s) foram ignoradas por conflito de turno.`,
          );
        } else if (!respMatriculas.ok) {
          toast.error('Inscrito salvo, mas houve um erro ao matriculá-lo nas turmas escolhidas.');
        }
      }

      if (result.matriculas_encerradas > 0) {
        toast.success(
          `Inscrito atualizado! ${result.matriculas_encerradas} matrícula(s) encerrada(s) pela troca de turno.`,
          8000,
        );
      } else {
        toast.success(
          modoEdicao ? 'Inscrito atualizado com sucesso!' : 'Inscrito criado com sucesso!',
        );
      }
      voltar();
    } catch (error) {
      console.error('Erro ao salvar aluno:', error);
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  if (carregandoAluno) {
    return (
      <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (semPermissao) {
    return (
      <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950 flex flex-col items-center justify-center gap-3 text-gray-500 dark:text-gray-400">
        <p className="text-sm">
          Você não tem permissão para {modoEdicao ? 'editar' : 'cadastrar'} inscritos.
        </p>
        <Button type="button" variant="secondary" onClick={voltar}>
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={voltar}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {modoEdicao
                ? `Editar Inscrito${novoAluno.nome ? `: ${novoAluno.nome}` : ''}`
                : 'Cadastrar Novo Inscrito'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {modoEdicao
                ? 'Altere os dados do inscrito e salve.'
                : 'Preencha os dados do inscrito — só o nome é obrigatório.'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Foto
            </h2>
            <FotoAluno
              alunoId={id}
              fotoUrl={novoAluno.foto_url}
              authHeaders={authHeaders}
              disabled={!modoEdicao}
              onFotoAtualizada={(url) => setNovoAluno((prev) => ({ ...prev, foto_url: url }))}
            />
          </section>

          <section className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-4 space-y-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Dados Básicos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                label="Nome Completo*"
                name="nome"
                value={novoAluno.nome}
                onChange={handleChange}
                onBlur={(e) => validarNome(e.target.value, true)}
                required
                error={Boolean(errosCampos.nome)}
                errorMessage={errosCampos.nome}
              />
              <Input
                type="date"
                label="Data de Nascimento"
                name="data_nascimento"
                value={novoAluno.data_nascimento}
                onChange={handleChange}
                error={Boolean(errosCampos.data_nascimento)}
                errorMessage={errosCampos.data_nascimento}
              />
              <Input
                type="date"
                label="Data de Cadastro"
                name="data_cadastro"
                value={novoAluno.data_cadastro}
                onChange={handleChange}
              />
              <Select
                label="Sexo"
                name="sexo"
                value={novoAluno.sexo}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Selecione...' },
                  { value: 'M', label: 'Masculino' },
                  { value: 'F', label: 'Feminino' },
                ]}
              />
              <Input
                label="Telefone"
                name="telefone"
                value={novoAluno.telefone}
                onChange={handleChange}
                error={Boolean(errosCampos.telefone)}
                errorMessage={errosCampos.telefone}
              />
              <Input
                label="Escola Atual"
                name="escola_atual"
                value={novoAluno.escola_atual}
                onChange={handleChange}
              />
            </div>
          </section>

          <section className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-4 space-y-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Matrícula
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Select
                label="Status"
                name="status"
                value={novoAluno.status}
                onChange={handleChange}
                options={[
                  { value: 'ativo', label: 'Ativo' },
                  { value: 'espera', label: 'Em espera' },
                  { value: 'inativo', label: 'Inativo' },
                ]}
              />
              {novoAluno.status === 'inativo' && (
                <Input
                  label="Motivo da Desistência*"
                  name="motivo_desistencia"
                  value={novoAluno.motivo_desistencia || ''}
                  onChange={handleChange}
                  placeholder="Ex: Mudança de endereço, incompatibilidade de horário..."
                  required
                  error={Boolean(errosCampos.motivo_desistencia)}
                  errorMessage={errosCampos.motivo_desistencia}
                />
              )}
              <Select
                label="Bolsista"
                name="bolsista"
                value={novoAluno.bolsista ? 'sim' : 'nao'}
                onChange={(e) =>
                  setNovoAluno((prev) => ({ ...prev, bolsista: e.target.value === 'sim' }))
                }
                options={[
                  { value: 'nao', label: 'Não' },
                  { value: 'sim', label: 'Sim (Bolsista)' },
                ]}
              />
              <Select
                label="Líder Servidor (Aluno Ajudante)"
                name="lider_servidor"
                value={novoAluno.lider_servidor ? 'sim' : 'nao'}
                onChange={(e) =>
                  setNovoAluno((prev) => ({ ...prev, lider_servidor: e.target.value === 'sim' }))
                }
                options={[
                  { value: 'nao', label: 'Não' },
                  { value: 'sim', label: 'Sim (Líder Servidor)' },
                ]}
              />
              <Input label="Turma" name="turma" value={novoAluno.turma} onChange={handleChange} />
              <Select
                label="Turno"
                name="turno"
                value={novoAluno.turno}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Selecione...' },
                  ...turnos.map((t) => ({ value: t, label: t })),
                ]}
              />
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Transporte
                </label>
                <div className="flex items-center gap-1.5">
                  <Select
                    name="transporte"
                    value={novoAluno.transporte}
                    onChange={handleChange}
                    className="flex-1"
                    options={[
                      { value: '', label: 'Selecione...' },
                      ...transportes.map((t) => ({ value: t.nome, label: t.nome })),
                      ...(novoAluno.transporte &&
                      !transportes.some((t) => t.nome === novoAluno.transporte)
                        ? [{ value: novoAluno.transporte, label: novoAluno.transporte }]
                        : []),
                    ]}
                  />
                  <button
                    type="button"
                    onClick={() => setModalTransportesAberto(true)}
                    title="Adicionar ou remover transportes"
                    className="w-10 h-10 flex-shrink-0 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors"
                  >
                    <Settings2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Ponto de Embarque
                </label>
                <div className="flex items-center gap-1.5">
                  <Select
                    name="ponto"
                    value={novoAluno.ponto}
                    onChange={handleChange}
                    className="flex-1"
                    options={[
                      { value: '', label: 'Selecione...' },
                      ...pontos.map((p) => ({ value: p.nome, label: p.nome })),
                      ...(novoAluno.ponto && !pontos.some((p) => p.nome === novoAluno.ponto)
                        ? [{ value: novoAluno.ponto, label: novoAluno.ponto }]
                        : []),
                    ]}
                  />
                  <button
                    type="button"
                    onClick={() => setModalPontosAberto(true)}
                    title="Adicionar ou remover pontos da lista"
                    className="w-10 h-10 flex-shrink-0 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors"
                  >
                    <Settings2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Ponto de Desembarque
                </label>
                <div className="flex items-center gap-1.5">
                  <Select
                    name="ponto_desembarque"
                    value={novoAluno.ponto_desembarque}
                    onChange={handleChange}
                    className="flex-1"
                    options={[
                      { value: '', label: novoAluno.ponto ? `Mesmo do embarque (${novoAluno.ponto})` : 'Selecione...' },
                      ...pontos.map((p) => ({ value: p.nome, label: p.nome })),
                      ...(novoAluno.ponto_desembarque && !pontos.some((p) => p.nome === novoAluno.ponto_desembarque)
                        ? [{ value: novoAluno.ponto_desembarque, label: novoAluno.ponto_desembarque }]
                        : []),
                    ]}
                  />
                </div>
              </div>
              <Input
                label="Acompanhamento"
                name="acompanhamento"
                value={novoAluno.acompanhamento}
                onChange={handleChange}
              />
            </div>

            {modoEdicao && matriculasAtuais.length > 0 && (
              <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
                  Matrículas atuais
                </h3>
                <div className="flex flex-wrap gap-2">
                  {matriculasAtuais.map((m) => (
                    <span
                      key={m.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300"
                    >
                      {m.nome_atividade || 'Turma'} — {m.dia_semana} {m.horario}
                      {m.turno?.toLowerCase() === 'noite' ? ' 🌙' : ''}
                    </span>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                  Pra cancelar ou mover uma matrícula, use as telas de Matrículas ou Grade por
                  Turma.
                </p>
              </div>
            )}

            <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={abrirInsercaoMatriculas}
                disabled={!novoAluno.turno}
                title={!novoAluno.turno ? 'Selecione o turno do inscrito primeiro' : undefined}
                className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-400 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CalendarPlus className="w-4 h-4" />
                Inserir Matrículas
                {totalMatriculasEscolhidas > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-blue-600 text-white text-xs font-semibold">
                    {totalMatriculasEscolhidas}
                  </span>
                )}
                {mostrarMatriculas ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {!novoAluno.turno && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                  Selecione o turno do inscrito pra ver os horários disponíveis.
                </p>
              )}

              {mostrarMatriculas && novoAluno.turno && (
                <div className="mt-4 space-y-3">
                  {carregandoAtividades ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Carregando turmas disponíveis...
                    </p>
                  ) : slotsPorDia.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Nenhum horário disponível pro turno {novoAluno.turno} ainda.
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Escolha de nenhum até todos os horários — o inscrito só é matriculado no que
                        você marcar aqui.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {slotsPorDia.map(({ dia, horarios: horariosDoDia }) => (
                          <div
                            key={dia}
                            className="rounded-2xl border border-gray-100 dark:border-gray-800 p-3 space-y-2"
                          >
                            <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                              {dia}
                            </h3>
                            {horariosDoDia.map(({ horario, opcoes }) => {
                              const chave = `${dia}|${horario}`;
                              return (
                                <div key={horario} className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400 dark:text-gray-500 w-12 flex-shrink-0">
                                    {horario}
                                  </span>
                                  <select
                                    value={matriculasEscolhidas[chave] || ''}
                                    onChange={(e) =>
                                      handleEscolherMatricula(dia, horario, e.target.value)
                                    }
                                    className="flex-1 h-9 px-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                  >
                                    <option key="default" value="">— Não matricular —</option>
                                    {opcoes.map((op) => {
                                      const opId = op.id ?? op.idatividades;
                                      return (
                                        <option key={opId} value={opId}>
                                          {op.nome}
                                          {op.turno?.toLowerCase() === 'noite'
                                            ? ' 🌙 Ensaio'
                                            : ''}{' '}
                                          {op.nome_professor ? `— ${op.nome_professor}` : ''} (
                                          {op.total_alunos}{' '}
                                          {op.total_alunos === 1 ? 'inscrito' : 'inscritos'})
                                        </option>
                                      );
                                    })}
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      {totalMatriculasEscolhidas > 0 && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 font-medium flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5" />
                          {totalMatriculasEscolhidas}{' '}
                          {totalMatriculasEscolhidas === 1
                            ? 'matrícula será criada'
                            : 'matrículas serão criadas'}{' '}
                          ao salvar.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-4 space-y-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Nível
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]"
                maxLength={1}
                label="Nível"
                name="nivel"
                value={novoAluno.nivel}
                onChange={handleDigitoUnicoChange}
                onKeyDown={handleDigitoUnicoKeyDown}
              />
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]"
                maxLength={1}
                label="Subnível"
                name="subnivel"
                value={novoAluno.subnivel}
                onChange={handleDigitoUnicoChange}
                onKeyDown={handleDigitoUnicoKeyDown}
              />
            </div>
          </section>

          <section className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-4 space-y-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Situação Anual ({anoSituacao})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                label="Situação de Matrícula"
                name="situacao_matricula"
                value={novoAluno.situacao_matricula}
                onChange={handleChange}
              />
              <Input
                label="Situação de Dívida"
                name="situacao_divida"
                value={novoAluno.situacao_divida}
                onChange={handleChange}
              />
            </div>
          </section>

          <section className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-4 space-y-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Saúde e Observações
            </h2>

            {modoEdicao && saudeExistente.length > 0 && (
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Observações já registradas
                </label>
                {saudeExistente.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-start gap-2 p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800"
                  >
                    <p className="flex-1 text-sm text-gray-700 dark:text-gray-300">{s.descricao}</p>
                    <button
                      type="button"
                      onClick={() => handleRemoverSaude(s.id)}
                      disabled={removendoSaudeId === s.id}
                      className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      {removendoSaudeId === s.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Laudos Médicos e Condições Especiais */}
            <div className="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Laudos Médicos & Condições Especiais (Selos para Educadores e Monitores)
                </label>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  Clique nas opções para ativar os selos correspondentes na Chamada e Grade:
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {LAUDOS_DISPONIVEIS.map(({ id: laudoId, label, icone }) => {
                  const ativo = Array.isArray(novoAluno.laudos) && novoAluno.laudos.includes(laudoId);
                  return (
                    <button
                      key={laudoId}
                      type="button"
                      onClick={() => handleToggleLaudo(laudoId)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                        ativo
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm ring-2 ring-blue-500/20'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500'
                      }`}
                    >
                      <span>{icone}</span>
                      <span>{label}</span>
                      {ativo ? <span className="ml-1 text-[10px] font-bold">✓</span> : <Plus className="w-3 h-3 ml-0.5 opacity-60" />}
                    </button>
                  );
                })}
              </div>

              <div className="pt-1">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Cuidados Especiais & Orientações Pedagógicas para Educadores/Monitores
                </label>
                <input
                  type="text"
                  name="cuidados_medicos"
                  value={novoAluno.cuidados_medicos || ''}
                  onChange={handleChange}
                  placeholder="Ex: Sensibilidade a sons altos; necessita de instruções diretas; etc."
                  className="w-full h-10 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs sm:text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  {modoEdicao ? 'Adicionar Nova Observação' : 'Observação de Saúde'}
                </label>
                <textarea
                  name="observacao_saude"
                  value={novoAluno.observacao_saude}
                  onChange={handleChange}
                  className="w-full min-h-[80px] px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  Informações Gerais
                </label>
                <textarea
                  name="informacoes_gerais"
                  value={novoAluno.informacoes_gerais}
                  onChange={handleChange}
                  className="w-full min-h-[80px] px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-y"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  Informações Adicionais
                </label>
                <textarea
                  name="Inf"
                  value={novoAluno.Inf}
                  onChange={handleChange}
                  className="w-full min-h-[80px] px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-y"
                />
              </div>
            </div>
          </section>

          <section className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-4 space-y-4">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Responsável Legal
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                label="Nome"
                name="responsavel_nome"
                value={novoAluno.responsavel_nome}
                onChange={handleChange}
              />
              <Input
                label="CPF"
                name="responsavel_cpf"
                value={novoAluno.responsavel_cpf}
                onChange={handleChange}
              />
              <Input
                label="RG"
                name="responsavel_rg"
                value={novoAluno.responsavel_rg}
                onChange={handleChange}
              />
              <Input
                type="date"
                label="Data de Nascimento"
                name="responsavel_data_nascimento"
                value={novoAluno.responsavel_data_nascimento}
                onChange={handleChange}
              />
              <Input
                label="Email"
                name="responsavel_email"
                value={novoAluno.responsavel_email}
                onChange={handleChange}
              />
              <Input
                label="Telefone"
                name="responsavel_telefone"
                value={novoAluno.responsavel_telefone}
                onChange={handleChange}
              />
              <Input
                label="Endereço"
                name="responsavel_endereco"
                value={novoAluno.responsavel_endereco}
                onChange={handleChange}
              />
              <Input
                label="Bairro"
                name="responsavel_bairro"
                value={novoAluno.responsavel_bairro}
                onChange={handleChange}
              />
              <Input
                label="CEP"
                name="responsavel_cep"
                value={novoAluno.responsavel_cep}
                onChange={handleChange}
              />
            </div>
          </section>

          <section className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Perfil Socioeconômico e Familiar (Serviço Social)
                </h2>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  Informações sociofamiliares para acompanhamento de vulnerabilidade e concessão de benefícios
                </p>
              </div>
              {novoAluno.renda_familiar &&
                novoAluno.moradores_casa &&
                Number(novoAluno.moradores_casa) > 0 && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium border border-blue-200 dark:border-blue-800">
                    <span>Renda per capita:</span>
                    <strong className="text-blue-900 dark:text-blue-100">
                      R$ {(Number(novoAluno.renda_familiar) / Number(novoAluno.moradores_casa)).toFixed(2)} / pessoa
                    </strong>
                  </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                type="number"
                step="0.01"
                min="0"
                label="Renda Familiar Total (R$)"
                name="renda_familiar"
                value={novoAluno.renda_familiar}
                onChange={handleChange}
                placeholder="Ex.: 1500.00"
              />
              <Input
                type="number"
                min="1"
                label="Moradores na Casa (Qtd.)"
                name="moradores_casa"
                value={novoAluno.moradores_casa}
                onChange={handleChange}
                placeholder="Ex.: 4"
              />
              <Select
                label="Condição de Moradia"
                name="moradia_tipo"
                value={novoAluno.moradia_tipo}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Selecione...' },
                  { value: 'propria', label: 'Própria' },
                  { value: 'alugada', label: 'Alugada' },
                  { value: 'cedida', label: 'Cedida / Emprestada' },
                  { value: 'ocupacao', label: 'Ocupação / Assentamento' },
                  { value: 'financiada', label: 'Financiada' },
                  { value: 'outro', label: 'Outro' },
                ]}
              />
              <Select
                label="Trabalho dos Responsáveis"
                name="trabalho_responsaveis"
                value={novoAluno.trabalho_responsaveis}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Selecione...' },
                  { value: 'formal_clt', label: 'Carteira Assinada (CLT)' },
                  { value: 'autonomo', label: 'Autônomo / Informal (Bico)' },
                  { value: 'desempregado', label: 'Desempregado(a)' },
                  { value: 'aposentado', label: 'Aposentado(a) / Pensionista' },
                  { value: 'outro', label: 'Outro' },
                ]}
              />
              <Select
                label="Benefício Social"
                name="recebe_beneficio"
                value={novoAluno.recebe_beneficio}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Selecione...' },
                  { value: 'nenhum', label: 'Não recebe benefício' },
                  { value: 'bolsa_familia', label: 'Bolsa Família / Auxílio Brasil' },
                  { value: 'bpc', label: 'BPC (Prestação Continuada)' },
                  { value: 'tarifa_social', label: 'Tarifa Social' },
                  { value: 'outro', label: 'Outro benefício' },
                ]}
              />
              <Input
                label="NIS (Número de Identificação Social)"
                name="nis"
                value={novoAluno.nis}
                onChange={handleChange}
                placeholder="Ex.: 12345678901"
              />
              <Select
                label="Grau de Vulnerabilidade"
                name="vulnerabilidade"
                value={novoAluno.vulnerabilidade}
                onChange={handleChange}
                options={[
                  { value: 'nao_informado', label: 'Não Informado / Em Avaliação' },
                  { value: 'baixa', label: 'Baixa Vulnerabilidade' },
                  { value: 'media', label: 'Média Vulnerabilidade' },
                  { value: 'alta', label: 'Alta Vulnerabilidade' },
                  { value: 'critica', label: 'Vulnerabilidade Crítica (Prioritária)' },
                ]}
              />
              <div className="md:col-span-2">
                <Input
                  label="Órgãos de Acompanhamento (Rede Externa)"
                  name="orgaos_acompanhamento"
                  value={novoAluno.orgaos_acompanhamento}
                  onChange={handleChange}
                  placeholder="Ex.: CRAS Central, Conselho Tutelar, UBS do Bairro"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                Parecer / Observações do Serviço Social
              </label>
              <textarea
                name="observacoes_social"
                value={novoAluno.observacoes_social}
                onChange={handleChange}
                rows={3}
                placeholder="Anotações técnicas, visitas ou observações sociofamiliares relevantes..."
                className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors resize-y"
              />
            </div>
          </section>

          <div className="flex justify-end gap-2 pb-6">
            <Button type="button" variant="secondary" onClick={voltar} disabled={salvando}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" icon={Save} disabled={salvando}>
              {salvando ? 'Salvando...' : modoEdicao ? 'Salvar Alterações' : 'Salvar Inscrito'}
            </Button>
          </div>
        </form>
      </main>

      <Modal
        isOpen={modalPontosAberto}
        onClose={() => setModalPontosAberto(false)}
        title="Pontos de Embarque"
        maxWidth="sm"
      >
        <div className="space-y-4">
          <div className="flex items-end gap-2">
            <Input
              label="Novo ponto"
              value={novoPontoNome}
              onChange={(e) => setNovoPontoNome(e.target.value)}
              placeholder="Ex.: Praça Central"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  adicionarPonto();
                }
              }}
              className="flex-1"
            />
            <Button
              type="button"
              variant="primary"
              icon={Plus}
              onClick={adicionarPonto}
              disabled={salvandoPonto || !novoPontoNome.trim()}
            >
              Adicionar
            </Button>
          </div>
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {pontos.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                Nenhum ponto cadastrado ainda.
              </p>
            ) : (
              pontos.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40"
                >
                  <span className="text-sm text-gray-900 dark:text-gray-100" translate="no">
                    {p.nome}
                  </span>
                  <button
                    type="button"
                    onClick={() => removerPonto(p)}
                    title="Remover"
                    className="w-7 h-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={modalTransportesAberto}
        onClose={() => setModalTransportesAberto(false)}
        title="Transportes"
        maxWidth="sm"
      >
        <div className="space-y-4">
          <div className="flex items-end gap-2">
            <Input
              label="Novo transporte"
              value={novoTransporteNome}
              onChange={(e) => setNovoTransporteNome(e.target.value)}
              placeholder="Ex.: Van 01, Ônibus Linha 2"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  adicionarTransporte();
                }
              }}
              className="flex-1"
            />
            <Button
              type="button"
              variant="primary"
              icon={Plus}
              onClick={adicionarTransporte}
              disabled={salvandoTransporte || !novoTransporteNome.trim()}
            >
              Adicionar
            </Button>
          </div>
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {transportes.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
                Nenhum transporte cadastrado ainda.
              </p>
            ) : (
              transportes.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40"
                >
                  <span className="text-sm text-gray-900 dark:text-gray-100" translate="no">
                    {t.nome}
                  </span>
                  <button
                    type="button"
                    onClick={() => removerTransporte(t)}
                    title="Remover"
                    className="w-7 h-7 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default CadastrarAluno;
